import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { TChargeUserType } from "types";
import useMaster from "hook/useMaster";
import useStaticMaster from "hook/useStaticMaster";
import { useHotelMarkupsData } from "libs/data-access";
import { flattenObject, showToast } from "utils/utility";

import { getRole } from "utils/routeUtility";
import { IHotelMarkupB2C, IHotelMarkupList, IHotelMarkupB2CDetails } from "./types";

export default function useHotelMarkupForm() {

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    // Get 'mno' query parameter
    const mno = searchParams.get('mno');
    const { service, userChargeType } = useParams<{ service: "flight" | "hotel", userChargeType: TChargeUserType }>();

    const {
        getHotelChargeApplyFor,
        hotelChargeApplyForDropdown,
        getTravelType,
        travelTypeDropdown,
        getMarkupFormCharges,
        markupFormChargesDropdown,
        getHotelCategories,
        hotelCategoriesDropdown,
        getHotelSuppliers,
        hotelSuppliersDropdown,
    } = useStaticMaster();
    const {
        allCountryList,
        getAllCountryList,
        hotelPlaces,
        getHotelPlacesByCountries
    } = useMaster();
    const {
        getHotelMarkupsData,
        postHotelMarkupsData,
        putHotelMarkupsData
    } = useHotelMarkupsData();


    const [markup, setMarkup] = useState<IHotelMarkupB2C>({
        id: 0,
        isLoading: false,
        details: {
            countryIds: [],
            placeIds: [],
            hotelSuppliers: [],
            affiliateGroupIds: [],
            markupChargeWithAmountTypes: [],
            hotelChargeApplyFor: "",
            hotelTypes: [],
            startDate: "",
            endDate: "",
            minimumGrossPrice: null,
            maximumGrossPrice: null,
            gst: getRole().includes("AFFILIATE") ? false : true,
            userType: "ADMIN_CUSTOMER",
            hotelCategories: []
        },
        rules: {
            countryIds: "NO",
            placeIds: "NO",
            hotelSuppliers: "NO",
            affiliateGroupIds: "NO",
            markupChargeWithAmountTypes: "YES",
            hotelChargeApplyFor: "YES",
            hotelTypes: "YES",
            startDate: "NO",
            endDate: "NO",
            minimumGrossPrice: "NO",
            maximumGrossPrice: "NO",
            gst: "NO",
            userType: "NO",
            hotelCategories: "NO"
        },
        errors: {
            countryIds: undefined,
            placeIds: undefined,
            hotelSuppliers: undefined,
            affiliateGroupIds: undefined,
            markupChargeWithAmountTypes: undefined,
            hotelChargeApplyFor: undefined,
            hotelTypes: undefined,
            startDate: undefined,
            endDate: undefined,
            minimumGrossPrice: undefined,
            maximumGrossPrice: undefined,
            gst: undefined,
            userType: undefined,
            hotelCategories: undefined
        }
    });


    useEffect(() => {
        if (service !== "hotel" || !userChargeType) {
            navigate(-1);
            return;
        };

        if (mno) {
            getHotelMarkupData();
        };

        getHotelChargeApplyFor();
        getMarkupFormCharges();
        getHotelCategories();
        getHotelSuppliers();
        getTravelType();
        getAllCountryList();

        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                userType: userChargeType
            }
        }));
    }, []);

    useEffect(() => {
        if (markupFormChargesDropdown.length > 0) {
            setMarkup(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    markupChargeWithAmountTypes: markupFormChargesDropdown.map(each => ({
                        markupFormCharge: String(each.value),
                        amount: 0,
                        feeType: "FIXED"
                    }))
                }
            }));
        };
    }, [markupFormChargesDropdown]);


    // HANDLER FUNCTIONS
    const convertResponseToDetails = (data: IHotelMarkupList): IHotelMarkupB2CDetails => {
        getHotelPlacesByCountries(data.countryResponses.map(c => String(c.id)));

        return {
            countryIds: data.countryResponses.map(c => c.id),
            placeIds: data.placeResponses.map(p => p.id),
            hotelSuppliers: data.hotelSuppliers.map(s => s.value),
            affiliateGroupIds: [], // data.affiliateGroup?.map(a => a?.id),
            markupChargeWithAmountTypes: data.markupChargeWithAmountTypeResponses.map(m => ({
                markupFormCharge: String(m.markupFormCharge.value),
                amount: m.amount,
                feeType: m.feeType
            })),
            hotelChargeApplyFor: String(data.hotelChargeApplyFor.value),
            hotelTypes: data.hotelTypes.map(t => t.value),
            startDate: data.startDate,
            endDate: data.endDate,
            minimumGrossPrice: data.minimumGrossPrice,
            maximumGrossPrice: data.maximumGrossPrice,
            gst: data.gst,
            userType: data.userType as TChargeUserType,
            hotelCategories: data.hotelCategories.map(c => c.value)
        };
    };

    const handleChange = (field: keyof IHotelMarkupB2C["details"], value: any) => {
        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                [field]: value
            },
            errors: {
                ...prev.errors,
                [field]: undefined
            }
        }));
    };

    const handleChangeMarkupChargeWithAmountTypes = (
        index: number,
        field: keyof IHotelMarkupB2C["details"]["markupChargeWithAmountTypes"][number],
        value: any
    ) => {
        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                markupChargeWithAmountTypes: prev.details.markupChargeWithAmountTypes.map((each, i) => {
                    if (i === index) {
                        return { ...each, [field]: value };
                    };
                    return each;
                })
            }
        }));
    };

    const validate = () => {
        const errors: IHotelMarkupB2C["errors"] = {
            countryIds: (
                markup.rules.countryIds === "YES"
                &&
                markup.details.countryIds.length === 0
            ) ? "Country is required" : undefined,
            placeIds: (
                markup.rules.placeIds === "YES"
                &&
                markup.details.placeIds.length === 0
            ) ? "Place is required" : undefined,
            hotelSuppliers: (
                markup.rules.hotelSuppliers === "YES"
                &&
                markup.details.hotelSuppliers.length === 0
            ) ? "Hotel supplier is required" : undefined,
            affiliateGroupIds: (
                markup.rules.affiliateGroupIds === "YES"
                &&
                markup.details.affiliateGroupIds.length === 0
            ) ? "Affiliate group is required" : undefined,
            markupChargeWithAmountTypes: (
                markup.rules.markupChargeWithAmountTypes === "YES"
                &&
                markup.details.markupChargeWithAmountTypes.length === 0
            ) ? "Markup charge with amount type is required" : undefined,
            hotelChargeApplyFor: (
                markup.rules.hotelChargeApplyFor === "YES"
                &&
                markup.details.hotelChargeApplyFor === ""
            ) ? "Hotel charge apply for is required" : undefined,
            hotelTypes: (
                markup.rules.hotelTypes === "YES"
                &&
                markup.details.hotelTypes.length === 0
            ) ? "Hotel type is required" : undefined,
            startDate: (
                markup.rules.startDate === "YES"
                &&
                markup.details.startDate === ""
            ) ? "Start date is required" : undefined,
            endDate: (
                markup.rules.endDate === "YES"
                &&
                markup.details.endDate === ""
            ) ? "End date is required" : undefined,
            minimumGrossPrice: (
                markup.rules.minimumGrossPrice === "YES"
                &&
                markup.details.minimumGrossPrice === 0
            ) ? "Minimum gross price is required" : undefined,
            maximumGrossPrice: (
                markup.rules.maximumGrossPrice === "YES"
                &&
                markup.details.maximumGrossPrice === 0
            ) ? "Maximum gross price is required" : undefined,
            gst: (
                markup.rules.gst === "YES"
                &&
                markup.details.gst === false
            ) ? "GST is required" : undefined,
            userType: (
                markup.rules.userType === "YES"
                &&
                !markup.details.userType
            ) ? "User type is required" : undefined,
            hotelCategories: (
                markup.rules.hotelCategories === "YES"
                &&
                markup.details.hotelCategories.length === 0
            ) ? "Hotel category is required" : undefined
        };

        const isAmountValid = markup.details.markupChargeWithAmountTypes.every(each => {
            if (!each.amount) {
                errors.markupChargeWithAmountTypes = "Commission charge is mandatory";
                return false;
            };
            return true;
        });

        setMarkup(prev => ({ ...prev, errors }));

        let isValid = Object.values(flattenObject(errors)).every(val => !val);

        if (!isAmountValid) {
            isValid = false;
            showToast({
                type: "error",
                message: "Commission charge is mandatory"
            });
        };

        // console.log("errors: ", errors);
        // console.log("isValid: ", isValid);

        return isValid;

    };

    const handleSaveMarkup = async () => {
        if (!validate()) return;

        if (markup.id) {
            await updateMarkup();
        } else {
            await saveMarkup();
        };
    };

    // API CALLS
    const getHotelMarkupData = async () => {
        const response = await getHotelMarkupsData(`/${mno}`);

        if (response.isSuccess) {
            // console.log("Response for get markup data: ", response.data?.data);
            const data: IHotelMarkupList = response.data?.data;

            const convertedDetails = convertResponseToDetails(data);

            setMarkup(prev => ({
                ...prev,
                id: data.id,
                details: convertedDetails
            }));
        } else {
            console.log("Error while getting hotel markup data: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting hotel markup data"
            });
        };
    };

    const saveMarkup = async () => {

        setMarkup(prev => ({ ...prev, isLoading: true }));

        const response = await postHotelMarkupsData(``, markup.details);

        setMarkup(prev => ({ ...prev, isLoading: false }));

        if (response.isSuccess) {
            navigate(-1);
        } else {
            console.log("Error while saving markup: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving markup"
            });
        };

        setMarkup(prev => ({ ...prev, isLoading: false }));

    };

    const updateMarkup = async () => {
        setMarkup(prev => ({ ...prev, isLoading: true }));

        const response = await putHotelMarkupsData(`/${markup.id}`, { ...markup.details, userType: null });

        setMarkup(prev => ({ ...prev, isLoading: false }));

        if (response.isSuccess) {
            navigate(-1);
        } else {
            console.log("Error while updating markup: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while updating markup"
            });
        };
    };

    return {
        navigate,
        hotelChargeApplyForDropdown,
        markupFormChargesDropdown,
        hotelCategoriesDropdown,
        hotelSuppliersDropdown,
        travelTypeDropdown,
        allCountryList,
        hotelPlaces,
        getHotelPlacesByCountries,

        handleChange,
        handleChangeMarkupChargeWithAmountTypes,
        markup,
        handleSaveMarkup
    };

}